import PropTypes from 'prop-types';
import { flushSync } from 'react-dom';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useRef,
  useState,
} from '@web-stories-wp/react';
import {
  generatePatternStyles,
  getLinearGradientPoints,
  isPatternEqual,
} from '@web-stories-wp/patterns';
import { useCanvas } from '../../app';
import { useTransform } from '../transform';
import FilterCanvasIndicator from '../canvas/filterCanvasIndicator';
import colorFormatter from '../richText/formatters/color';
import exportHTML from '../richText/customExport';

export function getTextGradient(
  root,
  element,
  style,
  pattern,
  editing,
  previewId,
  isRange = false,
  selectionRect
) {
  const kind = style === 'text-border-color' ? 'stroke' : 'fill';
  const suffix = editing ? 'edit' : 'display';
  const gradients = Array.from(
    root?.querySelectorAll('[data-gradient-bounds]') || []
  );
  const matches = gradients.filter((node) => {
    if (!node.id.includes(`-${element.id}-${suffix}`)) return false;
    const paint = JSON.parse(decodeURIComponent(node.dataset.gradientPattern));
    if (previewId) return paint.id === previewId;
    if (!isRange) return node.id.startsWith(`text-${kind}-gradient-`);
    return (
      node.id.startsWith('text-inline-gradient-') &&
      ((pattern.id && paint.id === pattern.id) ||
        isPatternEqual(paint, pattern))
    );
  });
  // A selected range can inherit the box fill without having an inline run yet.
  // Start from that paint frame; the first commit rebases it to the new run.
  if (isRange && !previewId && !matches.length) {
    return gradients.find((node) => {
      if (!node.id.startsWith(`text-fill-gradient-${element.id}-${suffix}`))
        return false;
      const paint = JSON.parse(
        decodeURIComponent(node.dataset.gradientPattern)
      );
      return isPatternEqual(paint, pattern);
    });
  }
  if (selectionRect && matches.length > 1) {
    const distance = (node) => {
      const { frame } = getTextGradientFrame(node, root);
      const rect = root.getBoundingClientRect();
      return Math.hypot(
        frame.x +
          frame.width / 2 +
          rect.left -
          selectionRect.left -
          selectionRect.width / 2,
        frame.y +
          frame.height / 2 +
          rect.top -
          selectionRect.top -
          selectionRect.height / 2
      );
    };
    matches.sort((a, b) => distance(a) - distance(b));
  }
  return matches[0];
}

function getSelectionRect(root, editorState) {
  if (!root || !editorState) return null;
  const selection = editorState.getSelection();
  const point = (key, offset) => {
    const block = Array.from(
      root.querySelectorAll('[data-block="true"][data-offset-key]')
    ).find((node) => node.dataset.offsetKey.startsWith(`${key}-`));
    if (!block) return null;
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (offset <= node.length) return { node, offset };
      offset -= node.length;
    }
    return null;
  };
  const start = point(selection.getStartKey(), selection.getStartOffset());
  const end = point(selection.getEndKey(), selection.getEndOffset());
  if (!start || !end) return null;
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range.getBoundingClientRect?.();
}

export function getTextGradientFrame(gradient, overlay) {
  const bounds = JSON.parse(gradient.dataset.gradientBounds);
  const matrix = gradient.ownerSVGElement.getScreenCTM();
  if (!matrix || !bounds.width || !bounds.height) return null;
  const rect = overlay.getBoundingClientRect();
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const width = bounds.width * Math.hypot(matrix.a, matrix.b);
  const height = bounds.height * Math.hypot(matrix.c, matrix.d);
  const rotationAngle = (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI;
  return {
    bounds,
    frame: {
      width,
      height,
      rotationAngle,
      x: matrix.a * cx + matrix.c * cy + matrix.e - rect.left - width / 2,
      y: matrix.b * cx + matrix.d * cy + matrix.f - rect.top - height / 2,
      transform: `rotate(${rotationAngle}deg)`,
    },
  };
}

// Legacy CSS text has no SVG definitions to measure. Read its existing paint
// frame without restyling it merely because the picker was opened.
export function getLegacyTextGradientFrame(
  root,
  target,
  element,
  pattern,
  isRange,
  selectionRect,
  editing
) {
  if (!root || !target || pattern.geometryVersion === 2) return null;
  const targetRect = target.getBoundingClientRect();
  const overlayRect = root.getBoundingClientRect();
  const angle = ((element.rotationAngle || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const scale =
    targetRect.width /
    (element.width * Math.abs(cos) + element.height * Math.abs(sin));
  if (!Number.isFinite(scale) || scale <= 0) return null;
  let rect = targetRect;
  let width = element.width;
  let height = element.height;
  if (isRange) {
    const paintRoot = editing
      ? root.querySelector('[aria-label="Edit layer"]')
      : root.querySelector(
          `[aria-label="Display layer"] [data-element-id="${element.id}"]`
        );
    const matches = Array.from(
      paintRoot?.querySelectorAll('span[style*="--gradient-pattern"]') || []
    ).filter((node) => {
      if (getComputedStyle(node).visibility === 'hidden') return false;
      const paint = JSON.parse(
        decodeURIComponent(node.style.getPropertyValue('--gradient-pattern'))
      );
      return (
        (pattern.id && paint.id === pattern.id) ||
        isPatternEqual(paint, pattern)
      );
    });
    if (selectionRect) {
      const distance = (node) => {
        const r = node.getBoundingClientRect();
        return Math.hypot(
          r.left + r.width / 2 - selectionRect.left - selectionRect.width / 2,
          r.top + r.height / 2 - selectionRect.top - selectionRect.height / 2
        );
      };
      matches.sort((a, b) => distance(a) - distance(b));
    }
    const node = matches[0];
    if (!node) return null;
    rect = node.getBoundingClientRect();
    const nodeScale =
      rect.width /
      (node.offsetWidth * Math.abs(cos) + node.offsetHeight * Math.abs(sin));
    width = (node.offsetWidth * nodeScale) / scale;
    height = (node.offsetHeight * nodeScale) / scale;
  }
  if (!width || !height) return null;
  const dx =
    rect.left + rect.width / 2 - targetRect.left - targetRect.width / 2;
  const dy =
    rect.top + rect.height / 2 - targetRect.top - targetRect.height / 2;
  return {
    bounds: {
      x: element.width / 2 + (dx * cos + dy * sin) / scale - width / 2,
      y: element.height / 2 + (-dx * sin + dy * cos) / scale - height / 2,
      width,
      height,
    },
    frame: {
      x: rect.left + rect.width / 2 - overlayRect.left - (width * scale) / 2,
      y: rect.top + rect.height / 2 - overlayRect.top - (height * scale) / 2,
      width: width * scale,
      height: height * scale,
      rotationAngle: element.rotationAngle || 0,
      transform: `rotate(${element.rotationAngle || 0}deg)`,
    },
  };
}

// A partial text selection may split a painted run. Preserve the physical
// handles when committing its geometry to that smaller run's local bounds.
export function rebaseGradient(pattern, from, to) {
  if (pattern.geometryVersion !== 2 || !from || !to) return pattern;
  // Repeated edits of the same run already use its local coordinates. Avoid
  // rounding drift that makes its SVG paint fail pattern lookup.
  if (['x', 'y', 'width', 'height'].every((key) => from[key] === to[key]))
    return pattern;
  const point = ({ x, y }) => ({
    x: (from.x + x * from.width - to.x) / to.width,
    y: (from.y + y * from.height - to.y) / to.height,
  });
  if (pattern.type === 'linear') {
    const { start, end } = getLinearGradientPoints(pattern, from);
    const local = ({ x, y }) => ({
      x: (x - to.x) / to.width,
      y: (y - to.y) / to.height,
    });
    return { ...pattern, start: local(start), end: local(end) };
  }
  return {
    ...pattern,
    center: point(pattern.center || { x: 0.5, y: 0.5 }),
    size: {
      w: ((pattern.size?.w ?? 1) * from.width) / to.width,
      h: ((pattern.size?.h ?? 1) * from.height) / to.height,
    },
  };
}

function GradientCanvasControls({
  pattern,
  element,
  bounds,
  changedStyle,
  editorState,
  pickerId,
  onCommit,
  onPaintMeasure,
  commitRef,
}) {
  const { nodesById, fullbleedContainer, canvasContainer, editingElement } =
    useCanvas(({ state }) => ({
      nodesById: state.nodesById,
      fullbleedContainer: state.fullbleedContainer,
      canvasContainer: state.canvasContainer,
      editingElement: state.editingElement,
    }));
  const {
    actions: { pushTransform },
  } = useTransform();
  const [textFrame, setTextFrame] = useState(null);
  const measuredTextFrame = useRef(null);
  const originalPageStyle = useRef(null);
  const previewActive = useRef(false);
  const previewFrame = useRef(null);
  const pendingPreview = useRef(null);
  const rangePreviewContent = useRef(null);
  const applyPreviewRef = useRef(null);
  const isRange =
    changedStyle === 'color' &&
    editorState &&
    !editorState.getSelection().isCollapsed();
  const previewId = `${pickerId}-range`;
  const isTextPaint =
    element?.type === 'text' &&
    ['color', 'text-border-color'].includes(changedStyle);
  const target = element?.isBackground
    ? fullbleedContainer
    : nodesById?.[element?.id];
  const overlay = canvasContainer || fullbleedContainer;
  const measurementInput = useRef(null);
  measurementInput.current = { element, pattern, editorState };

  useLayoutEffect(() => {
    if (!isTextPaint || !overlay) return undefined;
    let frame = null;
    let paintRoot = null;
    const measure = () => {
      frame = null;
      if (previewActive.current) return;
      const { element, pattern, editorState } = measurementInput.current;
      const selectionRect =
        isRange && getSelectionRect(canvasContainer, editorState);
      const gradient = getTextGradient(
        canvasContainer,
        element,
        changedStyle,
        pattern,
        editingElement === element.id,
        null,
        isRange,
        selectionRect
      );
      paintRoot =
        gradient?.closest('[data-element-id]') ||
        gradient?.ownerSVGElement ||
        (editingElement === element.id
          ? canvasContainer?.querySelector('[aria-label="Edit layer"]')
          : canvasContainer?.querySelector(
              `[aria-label="Display layer"] [data-element-id="${element.id}"]`
            )) ||
        target;
      const measured = gradient
        ? getTextGradientFrame(gradient, overlay)
        : getLegacyTextGradientFrame(
            canvasContainer,
            target,
            element,
            pattern,
            isRange,
            selectionRect,
            editingElement === element.id
          );
      const next = measured && {
        ...measured,
        legacyPaint: gradient ? 'svg' : 'css',
      };
      if (JSON.stringify(measuredTextFrame.current) !== JSON.stringify(next)) {
        measuredTextFrame.current = next;
        setTextFrame(next);
        onPaintMeasure?.(
          next && { bounds: next.bounds, legacyPaint: next.legacyPaint }
        );
      }
    };
    measure();
    const scheduleMeasure = () => {
      if (!previewActive.current && frame === null) {
        frame = requestAnimationFrame(measure);
      }
    };
    const observer = new MutationObserver(scheduleMeasure);
    // Watch the selected paint and its layout ancestors. Other layers and the
    // handle portal cannot change this paint frame.
    for (const node of new Set([target, paintRoot])) {
      if (!node) continue;
      observer.observe(node, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'style',
          'class',
          'transform',
          'x',
          'y',
          'width',
          'height',
          'viewBox',
          'data-gradient-bounds',
        ],
      });
      for (
        let parent = node.parentElement;
        parent;
        parent = parent.parentElement
      ) {
        observer.observe(parent, {
          attributes: true,
          attributeFilter: ['style', 'class', 'transform'],
        });
        if (parent === overlay) break;
      }
    }
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [
    element?.id,
    element?.content,
    element?.width,
    element?.height,
    element?.rotationAngle,
    isTextPaint,
    changedStyle,
    overlay,
    canvasContainer,
    editingElement,
    isRange,
    editorState,
    pattern.id,
    pattern.type,
    pattern.geometryVersion,
    target,
    onPaintMeasure,
  ]);

  const resetPreview = useCallback(() => {
    if (previewFrame.current !== null)
      cancelAnimationFrame(previewFrame.current);
    previewFrame.current = null;
    pendingPreview.current = null;
    rangePreviewContent.current = null;
    if (!previewActive.current) return;
    previewActive.current = false;
    if (element.isBackground) {
      fullbleedContainer.style.cssText = originalPageStyle.current;
      originalPageStyle.current = null;
    } else pushTransform(element.id, null);
  }, [element, fullbleedContainer, pushTransform]);
  const resetRef = useRef(resetPreview);
  resetRef.current = resetPreview;
  useEffect(() => () => resetRef.current(), []);

  const updatePattern = (getNext, { isPreview = false } = {}) => {
    const next = getNext(pattern);
    if (!isPreview) {
      if (isRange) {
        // A first legacy-range drag may mount SVG here. Its selected-run
        // bounds must exist before rebasing, including a one-move gesture.
        flushSync(() => applyPreviewRef.current(next));
      } else if (pendingPreview.current) {
        applyPreviewRef.current(pendingPreview.current);
      }
      const gradient =
        isRange &&
        getTextGradient(
          canvasContainer,
          element,
          changedStyle,
          pattern,
          editingElement === element.id,
          previewId
        );
      const committed = rebaseGradient(
        next,
        textFrame?.bounds,
        gradient && JSON.parse(gradient.dataset.gradientBounds)
      );
      resetPreview();
      onCommit(committed);
      return;
    }
    // Give the first movement immediate feedback, then coalesce further input
    // so high-frequency pointer events do not repeatedly format/layout text.
    if (!previewActive.current) {
      applyPreviewRef.current(next);
      return;
    }
    pendingPreview.current = next;
    if (previewFrame.current === null) {
      previewFrame.current = requestAnimationFrame(() => {
        previewFrame.current = null;
        const latest = pendingPreview.current;
        pendingPreview.current = null;
        if (latest) applyPreviewRef.current(latest);
      });
    }
  };
  applyPreviewRef.current = (next) => {
    if (!previewActive.current && element.isBackground)
      originalPageStyle.current = fullbleedContainer.style.cssText;
    previewActive.current = true;
    if (element.isBackground) {
      Object.assign(
        fullbleedContainer.style,
        generatePatternStyles(next, 'background', null, bounds)
      );
    } else if (isTextPaint) {
      if (isRange && !rangePreviewContent.current) {
        rangePreviewContent.current = exportHTML(
          colorFormatter.setters.setColor(editorState, {
            ...next,
            id: previewId,
          })
        );
      }
      const gradientPreview = isRange
        ? {
            content: rangePreviewContent.current,
            inlinePattern: { id: previewId, pattern: next },
            inlineBounds: { id: previewId, bounds: textFrame?.bounds },
          }
        : changedStyle === 'color'
        ? { color: next }
        : { textBorder: { ...element.textBorder, color: next } };
      pushTransform(element.id, {
        gradientPreview,
        staticTransformation: true,
      });
    } else
      pushTransform(element.id, {
        color: next,
        style: changedStyle,
        staticTransformation: true,
      });
  };
  // Rotate and canvas drags share preview measurement and range rebasing.
  useImperativeHandle(commitRef, () => (next) => updatePattern(() => next));
  const canDrag = target && overlay && (!isTextPaint || textFrame);
  return canDrag ? (
    <FilterCanvasIndicator
      pattern={pattern}
      element={element}
      box={{
        width: element.width,
        height: element.height,
        rotationAngle: element.rotationAngle,
      }}
      targetEl={target}
      overlayTarget={overlay}
      paintFrame={textFrame?.frame}
      legacyPaint={textFrame?.legacyPaint || 'css'}
      pickerId={pickerId}
      onUpdatePattern={updatePattern}
      onResetPatternPreview={resetPreview}
    />
  ) : null;
}

GradientCanvasControls.propTypes = {
  pattern: PropTypes.object.isRequired,
  element: PropTypes.object.isRequired,
  bounds: PropTypes.object,
  changedStyle: PropTypes.string.isRequired,
  editorState: PropTypes.object,
  pickerId: PropTypes.string.isRequired,
  onCommit: PropTypes.func.isRequired,
  onPaintMeasure: PropTypes.func,
  commitRef: PropTypes.object,
};
export default GradientCanvasControls;
